import { ChangeEvent, Dispatch, SetStateAction, useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import { Modal, Button, CardMedia, IconButton, Input } from "@mui/material";
import { useCreateBoxArtMutation, useUpdateBoxArtMutation, useUploadFileMutation, BoxArtItem } from "../store/api/commonAPI";
import { showToast, showError } from "../constants/toasts";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  editBoxArt?: BoxArtItem | null;
}

export default function AddBoxArt({
  open,
  onClose,
  setOpen,
  editBoxArt,
}: ModalProps) {
  const [image, setImage] = useState<string>("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [errors, setErrors] = useState<{ name?: string; image?: string }>({});

  const isEditMode = !!editBoxArt;

  // Upload file and create/update box art mutations
  const [uploadFile, { isLoading: isUploading }] = useUploadFileMutation();
  const [createBoxArt, { isLoading: isCreating }] = useCreateBoxArtMutation();
  const [updateBoxArt, { isLoading: isUpdating }] = useUpdateBoxArtMutation();

  const isLoading = isUploading || isCreating || isUpdating;

  // Reset form when modal opens/closes or when editBoxArt changes
  useEffect(() => {
    if (!open) {
      setImage("");
      setImageFile(null);
      setUploadedImageUrl("");
      setFileName("");
      setName("");
      setErrors({});
    } else if (editBoxArt) {
      // Populate form with edit data
      setName(editBoxArt.name || "");
      const existingImage = editBoxArt.image || editBoxArt.imageUrl || "";
      setImage(existingImage);
      setUploadedImageUrl(existingImage);
      setImageFile(null);
      setFileName("");
      setErrors({});
    } else {
      // Reset for add mode
      setImage("");
      setImageFile(null);
      setUploadedImageUrl("");
      setFileName("");
      setName("");
      setErrors({});
    }
  }, [open, editBoxArt]);

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = (event.target as HTMLInputElement).files;
    if (files && files[0]) {
      const file = files[0];
      
      // Validate file type
      if (!file.type.includes("image")) {
        setErrors((prev) => ({ ...prev, image: "Please select a valid image file" }));
        return;
      }

      // Validate file size (e.g., max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({ ...prev, image: "Image size should be less than 5MB" }));
        return;
      }

      setImageFile(file);
      setFileName(file.name);
      setImage(URL.createObjectURL(file));
      // Clear image error when user selects a new image
      if (errors.image) {
        setErrors((prev) => ({ ...prev, image: undefined }));
      }
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setName(newName);
    // Clear name error when user types
    if (errors.name) {
      setErrors((prev) => ({ ...prev, name: undefined }));
    }
  };

  // Validate form fields
  const validateForm = (): boolean => {
    const newErrors: { name?: string; image?: string } = {};

    // Validate name
    if (!name.trim()) {
      newErrors.name = "Name is required";
    } else if (name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    } else if (name.trim().length > 50) {
      newErrors.name = "Name must be less than 50 characters";
    }

    // Validate image - required for add mode, optional for edit mode (if already has image)
    if (!isEditMode && !imageFile) {
      newErrors.image = "Please upload an image";
    } else if (isEditMode && !imageFile && !uploadedImageUrl) {
      newErrors.image = "Please upload an image";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      showError("Please fix the errors in the form");
      return;
    }

    try {
      let imageUrl = uploadedImageUrl;

      // Step 1: Upload image if a new file is selected
      if (imageFile) {
        const uploadResult = await uploadFile({ file: imageFile }).unwrap();
        
        // Extract image URL from upload response (same pattern as profile page)
        imageUrl = uploadResult.data?.data || uploadResult.data;
        
        if (!imageUrl) {
          showError("Failed to get image URL from server.");
          return;
        }
      }

      // If no image URL available, show error
      if (!imageUrl) {
        showError("Please upload an image");
        return;
      }

      // Step 2: Create or update box art with image URL
      if (isEditMode && editBoxArt?._id) {
        // Update existing box art
        const updateResponse = await updateBoxArt({
          id: editBoxArt._id,
          name: name.trim(),
          image: imageUrl,
        }).unwrap();

        // Show success message from API response or default message
        const successMessage =
          updateResponse?.message || `Box Art "${name.trim()}" updated successfully`;
        showToast(successMessage);
      } else {
        // Create new box art
        const createResponse = await createBoxArt({
          name: name.trim(),
          image: imageUrl,
        }).unwrap();

        // Show success message from API response or default message
        const successMessage =
          createResponse?.message || `Box Art "${name.trim()}" created successfully`;
        showToast(successMessage);
      }

      // Reset form and close modal
      setImage("");
      setImageFile(null);
      setUploadedImageUrl("");
      setFileName("");
      setName("");
      setErrors({});
      setOpen(false);
    } catch (error: any) {
      console.error(`Error ${isEditMode ? "updating" : "creating"} box art:`, error);

      // Extract error message from API response or show default
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.message ||
        `Failed to ${isEditMode ? "update" : "create"} box art. Please try again.`;

      showError(errorMessage);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setOpen(false);
    }
  };

  const handleRemoveImage = () => {
    setImage("");
    setImageFile(null);
    setFileName("");
    if (errors.image) {
      setErrors((prev) => ({ ...prev, image: undefined }));
    }
  };

  return (
    <Modal
      className="modal addType_modal"
      open={open}
      onClose={handleClose}
    >
      <div className="modal-dialog">
        <div className="modal-body">
          <div className="modal_title">
            <h2>{isEditMode ? "Edit Box Art" : "Add Box Art"}</h2>
            <button
              type="button"
              className="btn-close"
              onClick={handleClose}
              aria-label="Close"
              disabled={isLoading}
            >
              <CloseIcon />
            </button>
          </div>

          <form className="form" onSubmit={handleSubmit}>
            <div className="gap_p">
              <div className="control_group">
                <label htmlFor="icon-button-file">Upload Image *</label>
                {image ? (
                  <div className="upload_image">
                    <div className="upload_image_holder">
                      <figure>
                        <CardMedia
                          component="img"
                          image={image}
                          alt="Uploaded image"
                        />
                      </figure>
                      <IconButton
                        aria-label="Remove uploaded image"
                        onClick={handleRemoveImage}
                        size="small"
                        disabled={isLoading}
                      >
                        <CloseIcon />
                      </IconButton>
                    </div>
                  </div>
                ) : (
                  <label
                    className="upload_image no_radius"
                    htmlFor="icon-button-file"
                  >
                    <Input
                      sx={{ display: "none" }}
                      id="icon-button-file"
                      type="file"
                      inputProps={{ accept: "image/png,image/jpeg,image/jpg" }}
                      onChange={handleImageChange}
                      disabled={isLoading}
                    />
                    <span className="upload_image_holder">
                      <figure className="add_img">
                        <img
                          src="/static/images/placeholder.png"
                          alt="placeholder"
                        />
                        <span className="upload_img_icon">
                          <img
                            src="/static/images/edit2_icon.svg"
                            alt="Upload"
                          />
                        </span>
                      </figure>
                    </span>
                  </label>
                )}
                {errors.image && (
                  <div style={{ color: "#d32f2f", fontSize: "0.75rem", marginTop: "4px" }}>
                    {errors.image}
                  </div>
                )}
              </div>

              <div className="control_group">
                <label htmlFor="name">Name *</label>
                <TextField
                  id="name"
                  fullWidth
                  hiddenLabel
                  placeholder="Enter Name"
                  value={name}
                  onChange={handleNameChange}
                  error={!!errors.name}
                  helperText={errors.name}
                  disabled={isLoading}
                  required
                />
              </div>
            </div>
            <div className="footer_btn_flex">
              <Button
                className="btn btn_grey"
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="btn btn_primary"
                disabled={isLoading}
              >
                {isLoading
                  ? isUploading
                    ? "Uploading..."
                    : isEditMode
                    ? "Updating..."
                    : "Creating..."
                  : isEditMode
                  ? "Update Box Art"
                  : "Add Box Art"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
